import { tgpu, d, std } from 'typegpu';
import type { TgpuRoot, TgpuBuffer, StorageFlag, UniformFlag } from 'typegpu';
import type { WgslArray } from 'typegpu/data';
import { ResonantStateA, ResonantStateB, ResonantParams, DataCell } from './types';
import { systemDeriv } from './deriv';
import { samplePerturb } from '../shared/hash';
import { NbParams } from '../shared/neighbor';
import type { NeighborPipelinesResult } from '../shared/DualStateSystem';

const TWO_PI = 6.28318530718;
const Rk4Result = d.struct({ t0: d.f32, om0: d.f32, t1: d.f32, om1: d.f32 });

export interface ResonantNeighborOpts {
  root: TgpuRoot;
  StateArrayA: WgslArray<typeof ResonantStateA>;
  StateArrayB: WgslArray<typeof ResonantStateB>;
  DataArray: WgslArray<typeof DataCell>;
  NbStackArrayA: WgslArray<typeof ResonantStateA>;
  NbStackArrayB: WgslArray<typeof ResonantStateB>;
  paramsBuffer: TgpuBuffer<typeof ResonantParams> & UniformFlag;
  nbParamsBuffer: TgpuBuffer<typeof NbParams> & UniformFlag;
  baseAa: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag;
  baseAb: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag;
  baseBa: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag;
  baseBb: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag;
  nbPertAa: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag;
  nbPertAb: TgpuBuffer<WgslArray<typeof ResonantStateA>> & StorageFlag;
  nbPertBa: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag;
  nbPertBb: TgpuBuffer<WgslArray<typeof ResonantStateB>> & StorageFlag;
  nbDataBuf: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag;
}

export function buildResonantNeighborPipelines(o: ResonantNeighborOpts): NeighborPipelinesResult {
  const root = o.root;

  const initLayout = tgpu.bindGroupLayout({
    baseA: { storage: o.StateArrayA, access: 'mutable' }, baseB: { storage: o.StateArrayB, access: 'mutable' },
    pertA: { storage: o.NbStackArrayA, access: 'mutable' }, pertB: { storage: o.NbStackArrayB, access: 'mutable' },
    data: { storage: o.DataArray, access: 'mutable' },
    params: { uniform: ResonantParams }, nb: { uniform: NbParams },
  });
  const stepLayout = tgpu.bindGroupLayout({
    baseCurA: { storage: o.StateArrayA }, baseCurB: { storage: o.StateArrayB },
    baseNextA: { storage: o.StateArrayA, access: 'mutable' }, baseNextB: { storage: o.StateArrayB, access: 'mutable' },
    pertCurA: { storage: o.NbStackArrayA }, pertCurB: { storage: o.NbStackArrayB },
    pertNextA: { storage: o.NbStackArrayA, access: 'mutable' }, pertNextB: { storage: o.NbStackArrayB, access: 'mutable' },
    data: { storage: o.DataArray, access: 'mutable' },
    params: { uniform: ResonantParams }, nb: { uniform: NbParams },
  });
  const il = initLayout; const sl = stepLayout;

  const circDiff = (a: number) => { 'use gpu'; return a - std.floor(a / TWO_PI + 0.5) * TWO_PI; };

  const integrate = (t0: number, w0: number, t1: number, w1: number, m0: number, m1: number, L0: number, L1: number, a0: number, dt: number) => {
    'use gpu';
    const d0 = systemDeriv(t0, w0, t1, w1, m0, m1, L0, L1, a0);
    const hw0 = w0 + 0.5 * dt * d0.da_om; const hw1 = w1 + 0.5 * dt * d0.db_om;
    const nt0 = t0 + dt * hw0; const nt1 = t1 + dt * hw1;
    const d1 = systemDeriv(nt0, hw0, nt1, hw1, m0, m1, L0, L1, a0);
    return Rk4Result({ t0: nt0, om0: hw0 + 0.5 * dt * d1.da_om, t1: nt1, om1: hw1 + 0.5 * dt * d1.db_om });
  };

  const nbInitCell = (ci: number) => {
    'use gpu';
    const p = il.$.params; const np = il.$.nb;
    const res = p.resolution;
    const x = ci % d.u32(res); const y = ci / d.u32(res);
    const u = d.f32(x) / res; const v = d.f32(y) / res;
    const omu = 1 - u; const omv = 1 - v;
    const th0 = omu * omv * p.cA00_th + u * omv * p.cA10_th + omu * v * p.cA01_th + u * v * p.cA11_th;
    const om0 = omu * omv * p.cA00_om + u * omv * p.cA10_om + omu * v * p.cA01_om + u * v * p.cA11_om;
    const th1 = omu * omv * p.cB00_th + u * omv * p.cB10_th + omu * v * p.cB01_th + u * v * p.cB11_th;
    const om1 = omu * omv * p.cB00_om + u * omv * p.cB10_om + omu * v * p.cB01_om + u * v * p.cB11_om;
    il.$.baseA[ci] = ResonantStateA({ theta0: th0, omega0: om0, dummy1: 0, dummy2: 0 });
    il.$.baseB[ci] = ResonantStateB({ theta1: th1, omega1: om1, dummy1: 0, dummy2: 0 });
    const K = d.u32(np.K); const cc = d.u32(res) * d.u32(res);
    const px = d.f32(x) / res; const py = d.f32(y) / res;
    for (let k = 0; k < K; k++) {
      const kf = d.f32(k);
      const h0 = samplePerturb(px * 1000 + np.seed + kf * 131.5, py * 1000 + np.seed, np.perturb, np.perturbMode);
      const h1 = samplePerturb(px * 1000 + 100 + np.seed + kf * 131.5, py * 1000 + np.seed, np.perturb, np.perturbMode);
      const idx = d.u32(k) * cc + ci;
      il.$.pertA[idx] = ResonantStateA({ theta0: th0 + h0, omega0: om0, dummy1: 0, dummy2: 0 });
      il.$.pertB[idx] = ResonantStateB({ theta1: th1 + h1, omega1: om1, dummy1: 0, dummy2: 0 });
    }
    il.$.data[ci] = DataCell({ r: 0, g: 0, b: 0, a: 0 });
  };



  const nbStepCell = (ci: number) => {
    'use gpu';
    const p = sl.$.params; const np = sl.$.nb; const dt = p.dt;
    const res = p.resolution; const cc = d.u32(res) * d.u32(res);
    const bA = sl.$.baseCurA[ci]; const bB = sl.$.baseCurB[ci];
    const bn = integrate(bA.theta0, bA.omega0, bB.theta1, bB.omega1, p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0, dt);
    sl.$.baseNextA[ci] = ResonantStateA({ theta0: bn.t0, omega0: bn.om0, dummy1: 0, dummy2: 0 });
    sl.$.baseNextB[ci] = ResonantStateB({ theta1: bn.t1, omega1: bn.om1, dummy1: 0, dummy2: 0 });
    const mode = np.nbMode;
    const K = d.u32(np.K);
    const px = d.f32(ci % d.u32(res)) / res; const py = d.f32(ci / d.u32(res)) / res;
    if (mode < 1.5) {
      let best = 0.0;
      for (let k = 0; k < K; k++) {
        const idx = d.u32(k) * cc + ci;
        const pA = sl.$.pertCurA[idx]; const pB = sl.$.pertCurB[idx];
        const pn = integrate(pA.theta0, pA.omega0, pB.theta1, pB.omega1, p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0, dt);
        sl.$.pertNextA[idx] = ResonantStateA({ theta0: pn.t0, omega0: pn.om0, dummy1: 0, dummy2: 0 });
        sl.$.pertNextB[idx] = ResonantStateB({ theta1: pn.t1, omega1: pn.om1, dummy1: 0, dummy2: 0 });
        const dd0 = circDiff(pn.t0 - bn.t0); const dw0 = pn.om0 - bn.om0;
        const dd1 = circDiff(pn.t1 - bn.t1); const dw1 = pn.om1 - bn.om1;
        const dist = std.sqrt(dd0*dd0 + dw0*dw0 + dd1*dd1 + dw1*dw1);
        if (dist > best) best = dist;
      }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: best, a: 1 });
    } else if (mode < 2.5) {
      let acc = 0.0;
      for (let k = 0; k < K; k++) {
        const idx = d.u32(k) * cc + ci;
        const pA = sl.$.pertCurA[idx]; const pB = sl.$.pertCurB[idx];
        const pn = integrate(pA.theta0, pA.omega0, pB.theta1, pB.omega1, p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0, dt);
        sl.$.pertNextA[idx] = ResonantStateA({ theta0: pn.t0, omega0: pn.om0, dummy1: 0, dummy2: 0 });
        sl.$.pertNextB[idx] = ResonantStateB({ theta1: pn.t1, omega1: pn.om1, dummy1: 0, dummy2: 0 });
        const dd0 = circDiff(pn.t0 - bn.t0); const dw0 = pn.om0 - bn.om0;
        const dd1 = circDiff(pn.t1 - bn.t1); const dw1 = pn.om1 - bn.om1;
        acc = acc + std.sqrt(dd0*dd0 + dw0*dw0 + dd1*dd1 + dw1*dw1);
      }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: acc, a: 1 });
    } else {
      const sigma = np.perturb;
      let sumLog = 0.0;
      for (let k = 0; k < K; k++) {
        const kf = d.f32(k);
        const h0 = samplePerturb(px * 1000 + np.seed + kf * 131.5, py * 1000 + np.seed, sigma, np.perturbMode);
        const h1 = samplePerturb(px * 1000 + 100 + np.seed + kf * 131.5, py * 1000 + np.seed, sigma, np.perturbMode);
        const pn = integrate(bA.theta0 + h0, bA.omega0, bB.theta1 + h1, bB.omega1, p.rpM0, p.rpM1, p.rpL0, p.rpL1, p.rpA0, dt);
        const dd0 = circDiff(pn.t0 - bn.t0); const dw0 = pn.om0 - bn.om0;
        const dd1 = circDiff(pn.t1 - bn.t1); const dw1 = pn.om1 - bn.om1;
        sumLog = sumLog + std.log(std.max(std.sqrt(dd0*dd0 + dw0*dw0 + dd1*dd1 + dw1*dw1), 1e-12) / sigma);
      }
      const gmLog = sumLog / np.K;
      const data = sl.$.data[ci];
      let count = data.a; let sumv = data.b;
      if (count < 0.5) { count = 1; sumv = gmLog; } else { count = count + 1; sumv = sumv + gmLog; }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: (sumv / count) / dt, a: count });
    }
  };

  return {
    nbInitPipeline: root.createGuardedComputePipeline(nbInitCell),
    nbStepPipeline: root.createGuardedComputePipeline(nbStepCell),
    nbInitBG: root.createBindGroup(initLayout, {
      baseA: o.baseAa, baseB: o.baseBa, pertA: o.nbPertAa, pertB: o.nbPertBa,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
    nbStepFwd: root.createBindGroup(stepLayout, {
      baseCurA: o.baseAa, baseCurB: o.baseBa, baseNextA: o.baseAb, baseNextB: o.baseBb,
      pertCurA: o.nbPertAa, pertCurB: o.nbPertBa, pertNextA: o.nbPertAb, pertNextB: o.nbPertBb,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
    nbStepBwd: root.createBindGroup(stepLayout, {
      baseCurA: o.baseAb, baseCurB: o.baseBb, baseNextA: o.baseAa, baseNextB: o.baseBa,
      pertCurA: o.nbPertAb, pertCurB: o.nbPertBb, pertNextA: o.nbPertAa, pertNextB: o.nbPertBa,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
  };
}
